import { Types } from 'mongoose';

import { IBaseEntity } from '../common';

import { IGatewayResponse } from './gateways';

export interface ITransactionRequest {
  merchant_id: string; // PayHere Merchant ID of the merchant
  order_id: string; // Order ID sent by Merchant to Checkout page
  firstName: string;
  lastName: string;
  address: string;
  city: string;
  country: string;
  description: string;
  email: string;
  phoneNumber: string;
}

export interface IPayhereCallbackResponse {
  merchant_id?: string; // PayHere Merchant ID of the merchant
  order_id?: string; // Order ID sent by Merchant to Checkout page
  payment_id?: string; // Unique Payment ID generated by PayHere for the processed payment
  payhere_amount?: string; // Total Amount of the payment
  payhere_currency?: string; // Currency code of the payment (LKR/USD/GBP/EUR/AUD)
  status_code?: number | string; // Payment status code (2, 0, -1, -2, -3)
  md5sig?: string; // Encrypted signature to verify the payment
  custom_1?: string; // Custom param 1 sent by merchant to Checkout page
  custom_2?: string; // Custom param 2 sent by merchant to Checkout page
  method?: string; // Payment method selected by the customer. (VISA, MASTER, AMEX, EZCASH, MCASH, GENIE, VISHWA, PAYAPP, HNB, FRIMI)
  status_message?: string; // Message received from payment gateway which the customer tried to pay
  // If the customer made the payment by VISA or MASTER credit/debit card, following parameters will also be available.
  card_holder_name?: string; // Card Holder Name
  card_no?: string; // Masked card number (Ex: ************4564)
  card_expiry?: string; // Card expiry in format MMYY (Ex: 0122)
}

export interface IPayhereUpdateRequest {
  merchantID?: string; // PayHere Merchant ID of the merchant
  orderID?: string; // Order ID sent by Merchant to Checkout page
  paymentID?: string; // Unique Payment ID generated by PayHere for the processed payment
  amount?: string; // Total Amount of the payment
  currency?: string; // Currency code of the payment (LKR/USD/GBP/EUR/AUD)
  statusCode?: number | string; // Payment status code (2, 0, -1, -2, -3)
  md5sig?: string; // Encrypted signature to verify the payment
  custom_1?: string; // Custom param 1 sent by merchant to Checkout page
  custom_2?: string; // Custom param 2 sent by merchant to Checkout page
  method?: string; // Payment method selected by the customer. (VISA, MASTER, AMEX, EZCASH, MCASH, GENIE, VISHWA, PAYAPP, HNB, FRIMI)
  statusMessage?: string; // Message received from payment gateway which the customer tried to pay
  // If the customer made the payment by VISA or MASTER credit/debit card, following parameters will also be available.
  cardHolderName?: string; // Card Holder Name
  cardNo?: string; // Masked card number (Ex: ************4564)
  cardExpiry?: string; // Card expiry in format MMYY (Ex: 0122)
}

export interface ITransaction extends IPayhereUpdateRequest, IBaseEntity {
  items?: string;
  status?: TRANSACTION_STATUS;
  paymentType?: PAYMENT_TYPE;
  firstName?: string;
  lastName?: string;
  address?: string;
  city?: string;
  country?: string;
  email?: string;
  phoneNumber?: string;
}

export type ITransactionUpdateResponse = Omit<
  ITransaction,
  | 'md5sig'
  | 'cardExpiry'
  | 'statusMessage'
  | 'custom_1'
  | 'custom_2'
  | 'statusCode'
>;
export interface ITransactionResponse extends ITransaction {
  gateway: IGatewayResponse;
}

export interface ITransactionOrderRequest {
  currency?: string;
  payingAmount?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  address?: string;
  city?: string;
  country?: string;
  description?: string;
  phoneNumber?: {
    e164Number: string;
    number?: string;
    internationalNumber?: string;
  };
  paymentType?: PAYMENT_TYPE;
  recurringFormData?: {
    firstPaymentDate?: Date;
    finalPaymentDate?: Date;
    noOfTransfers?: number;
    frequency?: string;
  };
  selectedPaymentMethod?: {
    title?: string;
    isRecurring?: boolean;
    _id?: string;
  };
  orderID?: string;
  status?: TRANSACTION_STATUS;
  merchantID?: string;
  invoicesList?: ITransactionInvoice[];
  separateReceipts?: boolean;
  paymentOption?: RECEIPTS_PAYMENT_OPTIONS;
  policyNo?: string;
  transactionId?: string;
}

interface ITransactionInvoice {
  id: Types.ObjectId | string;
  amount: number;
  invoiceId: string;
}

export enum TRANSACTION_STATUS {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
  CHARGED_BACK = 'CHARGED_BACK',
}

export enum PAYMENT_TYPE {
  ONE_TIME = 'ONE_TIME',
  RECURRING = 'RECURRING',
}

export enum RECEIPTS_PAYMENT_OPTIONS {
  PREMIUM = 'PREMIUM',
  DEPOSIT = 'DEPOSIT',
  MISCELLANEOUS = 'MISCELLANEOUS',
}

export interface ITransactionStatusRequest {
  status: TRANSACTION_STATUS;
}
